/**
 * PRD-26 — per-kind icon + tone for the notification card UI.
 *
 * Visual lexicon mirrors the gradebook drawer's pill tones (PR #125):
 * success → emerald, warning → amber, danger → red, info → indigo,
 * neutral → slate. Keeping the palette consistent means a professor
 * who has internalized "amber = needs attention soon" on the
 * Calificaciones page sees the same signal in the bell.
 */

import {
  AlertTriangle,
  BookOpenCheck,
  CalendarClock,
  CheckCircle2,
  ClipboardCheck,
  GraduationCap,
  HelpCircle,
  Mail,
  PartyPopper,
  Send,
  Sparkles,
  TriangleAlert,
  Upload,
  UserCheck,
  UserPlus,
  Zap,
  type LucideIcon,
} from "lucide-react"

import type { NotificationKind } from "@/types"

export type NotificationTone = "success" | "warning" | "danger" | "info" | "neutral"

interface KindVisual {
  Icon: LucideIcon
  tone: NotificationTone
}

const FALLBACK: KindVisual = { Icon: Sparkles, tone: "info" }

const REGISTRY: Record<NotificationKind, KindVisual> = {
  "material.ready": { Icon: BookOpenCheck, tone: "success" },
  "material.generation_failed": { Icon: TriangleAlert, tone: "danger" },
  "material.scheduled_dispatched": { Icon: Zap, tone: "info" },
  "material.scheduled_failed": { Icon: AlertTriangle, tone: "danger" },
  "material.scheduled_auto_sent": { Icon: Send, tone: "success" },
  "material.due_today": { Icon: CalendarClock, tone: "warning" },
  "material.deadline_approaching": { Icon: CalendarClock, tone: "warning" },
  // PRD-42 §FR-7.1 — students who joined after the material generated
  // and never received it. Amber, not red: the send itself succeeded,
  // but somebody is missing and only the professor can close the gap.
  "material.roster_gap": { Icon: UserPlus, tone: "warning" },
  "material.send_failed": { Icon: Mail, tone: "danger" },
  // PRD-41 §FR-6 — same amber "needs your attention" tone as the
  // per-upload review kind below; both land on the review queue.
  "material.review_ready": { Icon: ClipboardCheck, tone: "warning" },
  "grading.review_needed": { Icon: ClipboardCheck, tone: "warning" },
  "grading.completed_auto": { Icon: CheckCircle2, tone: "success" },
  "portal.student_uploaded": { Icon: Upload, tone: "info" },
  "student.survey_completed": { Icon: UserCheck, tone: "info" },
  welcome: { Icon: PartyPopper, tone: "success" },
}

export function visualForKind(kind: string): KindVisual {
  return REGISTRY[kind as NotificationKind] ?? FALLBACK
}

// Tailwind class tokens per tone. The icon chip uses the bg+text
// combo; the unread dot pulls the same accent so the card reads as
// one chromatic unit.
export const TONE_CLASSES: Record<NotificationTone, { chip: string; dot: string }> = {
  success: { chip: "bg-success-soft text-success-ink", dot: "bg-success" },
  warning: { chip: "bg-attention-soft text-attention-ink", dot: "bg-attention" },
  danger: { chip: "bg-danger-soft text-danger-ink", dot: "bg-danger" },
  info: { chip: "bg-accent-soft text-accent-ink", dot: "bg-accent" },
  neutral: { chip: "bg-inset text-ink-muted", dot: "bg-border-strong" },
}

// Re-exports to keep imports terse in components.
export {
  AlertTriangle,
  BookOpenCheck,
  CalendarClock,
  CheckCircle2,
  ClipboardCheck,
  GraduationCap,
  HelpCircle,
  Mail,
  PartyPopper,
  Send,
  Sparkles,
  TriangleAlert,
  Upload,
  UserCheck,
  UserPlus,
  Zap,
}
